import { createContext, useContext } from 'react';

import { noop } from '@adsk/alloy-react-helpers';

import { type Attachment } from '../../types';

export type AttachmentsViewerContext = {
  open: boolean;
  currentAttachment: Attachment | undefined;
  openViewer: (attachmentId: string) => void;
  viewNextAttachment: () => void;
  viewPreviousAttachment: () => void;
  closeViewer: () => void;
  /** Internal: notifies the `onFileHandled` prop. Not part of the public hook. */
  reportFileHandled: () => void;
  totalAttachments: number;
  currentAttachmentIndex: number;
  currentZoomLevel: number;
  zoomIn: () => void;
  zoomOut: () => void;
};

export const AttachmentsViewerContext = createContext<AttachmentsViewerContext>(
  {
    open: true,
    currentAttachment: undefined,
    openViewer: noop,
    viewNextAttachment: noop,
    viewPreviousAttachment: noop,
    closeViewer: noop,
    reportFileHandled: noop,
    totalAttachments: 0,
    currentAttachmentIndex: 0,
    currentZoomLevel: 100,
    zoomIn: noop,
    zoomOut: noop,
  },
);

export const useAttachmentsViewerContext = () =>
  useContext(AttachmentsViewerContext);

/** Public hook to access attachments viewer functionality for deep linking and programmatic access */
export const useAttachmentsViewer = () => {
  const { openViewer, closeViewer, currentAttachmentIndex, open } =
    useAttachmentsViewerContext();

  return {
    /** Open a specific attachment by ID (e.g., for deep linking) */
    openViewer,
    /** Close the viewer */
    closeViewer,
    /** Index of currently displayed attachment (0-based) */
    currentAttachmentIndex,
    /** Whether the viewer is currently open */
    open,
  };
};
